import { useCallback, useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import {
  deleteAll,
  deleteNotification,
  markAllAsRead,
  readNotification,
  unreadNotification,
} from '~/legacy-components/LegacyNotificationList/useLegacyNotifications';
import { People } from '~/services/Http';
import {
  GetNotificationsProps,
  useGetNotifications,
} from '~/services/Http/hooks';
export const revalidateNotificationsInterval = 60 * 1000;

export interface UseGetNotificationListOutput {
  notifications?: People.INotificationDto[];
  loading: boolean;
  failedToLoad: boolean;
  triggerMutation: (data: People.INotificationDto[]) => void;
  onMarkAsUnread: (id: number) => void;
  onMarkAsRead: (id: number) => void;
  onDeleteNotification: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDeleteAll: () => void;
}

export const useNotificationList = (
  props?: GetNotificationsProps,
  swrConfig?: SWRConfiguration<People.INotificationDto[]>,
): UseGetNotificationListOutput => {
  const config = useMemo(
    () => ({ refreshInterval: revalidateNotificationsInterval, ...swrConfig }),
    [swrConfig],
  );
  const {
    data: notifications,
    loading,
    failedToLoad,
    mutate,
  } = useGetNotifications(props, config);

  const triggerMutation = useCallback(
    (data: People.INotificationDto[]) => {
      mutate(data, false);
    },
    [mutate],
  );

  // Single notification actions
  const handleUpdateNotificationsReadState = (
    id: string | number,
    isRead: boolean,
  ) => {
    if (!notifications) {
      return triggerMutation([]);
    }

    const updatedNotifications = notifications.map((n) => {
      if (n.id !== id) {
        return n;
      }

      return { ...n, isRead };
    });

    triggerMutation(updatedNotifications);
  };

  const onMarkAsUnread = (id: number) => {
    unreadNotification(id).then(() =>
      handleUpdateNotificationsReadState(id, false),
    );
  };

  const onMarkAsRead = (id: number) => {
    readNotification(id).then(() =>
      handleUpdateNotificationsReadState(id, true),
    );
  };

  const onDeleteNotification = (id: number) => {
    deleteNotification(id).then(() => {
      triggerMutation(notifications?.filter((n) => n.id !== id) || []);
    });
  };

  // All notifications actions

  const onMarkAllAsRead = () => {
    markAllAsRead().then(() => {
      triggerMutation(
        notifications ? notifications.map((n) => ({ ...n, isRead: true })) : [],
      );
    });
  };

  const onDeleteAll = () => {
    deleteAll().then(() => {
      triggerMutation([]);
    });
  };

  return {
    notifications,
    loading,
    failedToLoad,
    triggerMutation,
    onMarkAsUnread,
    onMarkAsRead,
    onDeleteNotification,
    onMarkAllAsRead,
    onDeleteAll,
  };
};
